import React, { useState, useEffect, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Menu, X, Globe } from "lucide-react";
import LumiereIcon from "@/components/LumiereIcon";
import { useLanguage } from "@/i18n";

const Navbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isLangOpen, setIsLangOpen] = useState(false);
  const langDesktopRef = useRef(null);
  const langMobileRef = useRef(null);
  const { language, setLanguage, t, supportedLanguages, languageLabels } = useLanguage();

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (
        langDesktopRef.current && !langDesktopRef.current.contains(e.target) &&
        langMobileRef.current && !langMobileRef.current.contains(e.target)
      ) {
        setIsLangOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const navLinks = [
    { label: t('nav.openclaw'), href: "#openclaw" },
    { label: t('nav.features'), href: "#features" },
    { label: t('nav.providers'), href: "#providers" },
    { label: t('nav.themes'), href: "#themes" },
    { label: t('nav.privacy'), href: "#privacy" },
  ];

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled
          ? "bg-background/80 backdrop-blur-xl border-b border-border/50"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <nav className="flex items-center justify-between h-16 lg:h-20">
          {/* Logo */}
          <a href="#" className="flex items-center gap-2 group">
            <div className="relative">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-primary to-accent flex items-center justify-center">
                <LumiereIcon className="w-5 h-5 text-primary-foreground" />
              </div>
              <div className="absolute inset-0 rounded-xl bg-gradient-to-br from-primary to-accent opacity-50 blur-lg group-hover:opacity-70 transition-opacity" />
            </div>
            <span className="text-xl font-display font-bold text-foreground">
              Lumiere
            </span>
          </a>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-8">
            {navLinks.map((link) => (
              <a
                key={link.label}
                href={link.href}
                className="text-muted-foreground hover:text-foreground transition-colors duration-200 text-sm font-medium"
              >
                {link.label}
              </a>
            ))}
          </div>

          {/* Desktop CTA */}
          <div className="hidden lg:flex items-center gap-3">
            {/* Language Switcher */}
            <div className="relative" ref={langDesktopRef}>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setIsLangOpen(!isLangOpen)}
                className="flex items-center gap-2"
              >
                <Globe className="w-4 h-4" />
                <span className="font-medium">{languageLabels[language]}</span>
              </Button>
              {isLangOpen && (
                <div className="absolute top-full right-0 mt-2 py-1 min-w-[120px] rounded-lg border border-border/50 bg-background/95 backdrop-blur-xl shadow-lg z-50">
                  {supportedLanguages.map((lang) => (
                    <button
                      key={lang}
                      onClick={() => { setLanguage(lang); setIsLangOpen(false); }}
                      className={`w-full text-left px-4 py-2 text-sm transition-colors hover:bg-muted/50 ${
                        lang === language ? 'text-primary font-medium' : 'text-muted-foreground'
                      }`}
                    >
                      {languageLabels[lang]}
                    </button>
                  ))}
                </div>
              )}
            </div>
            
            <Button variant="ghost" size="sm" asChild>
              <a
                href="https://github.com/ajamaica/lumiere"
                target="_blank"
                rel="noopener noreferrer"
              >
                {t('nav.github')}
              </a>
            </Button>
            <Button variant="hero-outline" size="sm" asChild>
              <a
                href="https://chromewebstore.google.com/detail/lumiere/ppnnimejlhphcjckocpiinjmpmdfhhmg"
                target="_blank"
                rel="noopener noreferrer"
              >
                <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 3c1.49 0 2.87.47 4.01 1.26L7.8 17.65A7.007 7.007 0 0 1 5 12c0-3.87 3.13-7 7-7zm0 14c-1.49 0-2.87-.47-4.01-1.26L16.2 6.35A7.007 7.007 0 0 1 19 12c0 3.87-3.13 7-7 7z"/>
                  <circle cx="12" cy="12" r="3.5"/>
                </svg>
                {t('nav.chromeExtension')}
              </a>
            </Button>
            <Button variant="hero" size="sm" asChild>
              <a
                href="https://apps.apple.com/app/lumiere-openclaw-ai-agents/id6758534255"
                target="_blank"
                rel="noopener noreferrer"
              >
                <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M18.71 19.5C17.88 20.74 17 21.95 15.66 21.97C14.32 22 13.89 21.18 12.37 21.18C10.84 21.18 10.37 21.95 9.09997 22C7.78997 22.05 6.79997 20.68 5.95997 19.47C4.24997 17 2.93997 12.45 4.69997 9.39C5.56997 7.87 7.12997 6.91 8.81997 6.88C10.1 6.86 11.32 7.75 12.11 7.75C12.89 7.75 14.37 6.68 15.92 6.84C16.57 6.87 18.39 7.1 19.56 8.82C19.47 8.88 17.39 10.1 17.41 12.63C17.44 15.65 20.06 16.66 20.09 16.67C20.06 16.74 19.67 18.11 18.71 19.5ZM13 3.5C13.73 2.67 14.94 2.04 15.94 2C16.07 3.17 15.6 4.35 14.9 5.19C14.21 6.04 13.07 6.7 11.95 6.61C11.8 5.46 12.36 4.26 13 3.5Z" />
                </svg>
                {t('nav.download')}
              </a>
            </Button>
          </div>

          {/* Mobile Menu Button */}
          <div className="flex lg:hidden items-center gap-2">
            {/* Mobile Language Switcher */}
            <div className="relative" ref={langMobileRef}>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setIsLangOpen(!isLangOpen)}
                className="flex items-center gap-1"
              >
                <Globe className="w-4 h-4" />
                <span className="text-xs font-medium">{languageLabels[language]}</span>
              </Button>
              {isLangOpen && (
                <div className="absolute top-full right-0 mt-2 py-1 min-w-[100px] rounded-lg border border-border/50 bg-background/95 backdrop-blur-xl shadow-lg z-50">
                  {supportedLanguages.map((lang) => (
                    <button
                      key={lang}
                      onClick={() => { setLanguage(lang); setIsLangOpen(false); }}
                      className={`w-full text-left px-3 py-2 text-xs transition-colors hover:bg-muted/50 ${
                        lang === language ? 'text-primary font-medium' : 'text-muted-foreground'
                      }`}
                    >
                      {languageLabels[lang]}
                    </button>
                  ))}
                </div>
              )}
            </div>
            
            <button
              className="p-2 text-foreground"
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              aria-label="Toggle menu"
            >
              {isMobileMenuOpen ? (
                <X className="w-6 h-6" />
              ) : (
                <Menu className="w-6 h-6" />
              )}
            </button>
          </div>
        </nav>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <div className="lg:hidden pb-6 animate-fade-in">
            <div className="flex flex-col gap-4 pt-4 border-t border-border/50">
              {navLinks.map((link) => (
                <a
                  key={link.label}
                  href={link.href}
                  className="text-muted-foreground hover:text-foreground transition-colors py-2 text-base"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  {link.label}
                </a>
              ))}
              <div className="flex flex-col gap-3 pt-4">
                <Button variant="hero-outline" asChild>
                  <a
                    href="https://github.com/ajamaica/lumiere"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {t('nav.github')}
                  </a>
                </Button>
                <Button variant="hero" asChild>
                  <a
                    href="https://chromewebstore.google.com/detail/lumiere/ppnnimejlhphcjckocpiinjmpmdfhhmg"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 3c1.49 0 2.87.47 4.01 1.26L7.8 17.65A7.007 7.007 0 0 1 5 12c0-3.87 3.13-7 7-7zm0 14c-1.49 0-2.87-.47-4.01-1.26L16.2 6.35A7.007 7.007 0 0 1 19 12c0 3.87-3.13 7-7 7z"/>
                      <circle cx="12" cy="12" r="3.5"/>
                    </svg>
                    {t('nav.chromeExtension')}
                  </a>
                </Button>
                <Button variant="hero" asChild>
                  <a
                    href="https://apps.apple.com/app/lumiere-openclaw-ai-agents/id6758534255"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M18.71 19.5C17.88 20.74 17 21.95 15.66 21.97C14.32 22 13.89 21.18 12.37 21.18C10.84 21.18 10.37 21.95 9.09997 22C7.78997 22.05 6.79997 20.68 5.95997 19.47C4.24997 17 2.93997 12.45 4.69997 9.39C5.56997 7.87 7.12997 6.91 8.81997 6.88C10.1 6.86 11.32 7.75 12.11 7.75C12.89 7.75 14.37 6.68 15.92 6.84C16.57 6.87 18.39 7.1 19.56 8.82C19.47 8.88 17.39 10.1 17.41 12.63C17.44 15.65 20.06 16.66 20.09 16.67C20.06 16.74 19.67 18.11 18.71 19.5ZM13 3.5C13.73 2.67 14.94 2.04 15.94 2C16.07 3.17 15.6 4.35 14.9 5.19C14.21 6.04 13.07 6.7 11.95 6.61C11.8 5.46 12.36 4.26 13 3.5Z" />
                    </svg>
                    {t('nav.download')}
                  </a>
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </header>
  );
};

export default Navbar;
